<template>
  <figure class="max-pos-datatable" data-max-host>
    <figcaption v-if="node.title" class="max-pos-datatable__title">{{ node.title }}</figcaption>
    <div class="max-pos-datatable__wrap tabulator-parent">
      <div ref="table" class="tabulator"></div>
    </div>
  </figure>
</template>

<script>
import { nextTick } from 'vue'
import { loadTabulator } from '~/lib/tabulator'
import escapeHtml from '~/lib/escapehtml'
import { useMaxContext, useMaxInterface } from '#max-runtime/context.ts'
import { formatValue, normalizeTime } from '#max-core'

const BUTTON_CLASS = {
  neutral: 'btn-default',
  accent: 'btn-primary',
  positive: 'btn-success',
  warning: 'btn-warning',
  negative: 'btn-danger'
}
const NUMERIC = ['number', 'integer', 'decimal', 'currency', 'percent', 'duration']
const INTERACTIVE = 'input, button, select, textarea, label, a, .tabulator-row-handle, .max-pos-datatable__check'

export default {
  props: ['node'],
  setup () {
    const { $translate } = useNuxtApp()
    return { iface: useMaxInterface(), ctx: useMaxContext(), _: $translate, BUTTON_CLASS }
  },
  data () {
    return {
      tabulator: null,
      paging: false
    }
  },
  computed: {
    selectable () {
      return Array.isArray(this.node.selected)
    },
    selected () {
      return new Set(this.node.selected || [])
    },
    sortable () {
      return typeof this.node.sort === 'string'
    },
    paged () {
      return typeof this.node.pageState === 'string'
    },
    page () {
      const n = Number(this.node.page)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    pageSize () {
      return typeof this.node.pageSize === 'number' ? this.node.pageSize : 50
    },
    pages () {
      return Math.max(1, Math.ceil((this.node.total || 0) / this.pageSize))
    },
    catalog () {
      return (this.ctx && this.ctx.labels && this.ctx.labels.catalog) || {}
    },
    locale () {
      return this.$store && this.$store.getters.locale
    },
    signature () {
      const cols = (this.node.columns || []).map(c => [c.key, c.edit && c.edit.input, c.format].join(':')).join('|')
      return [cols, this.selectable, this.sortable, this.paged, this.pageSize, !!(this.node.rowActions && this.node.rowActions.length)].join('/')
    },
    dataSignature () {
      return (this.node.resolvedRows || []).map(row => row.key + ':' + JSON.stringify(row.cells) + ':' + (row.actions || []).join(',')).join('|') + '#' + this.page + '#' + (this.node.total || 0)
    }
  },
  watch: {
    dataSignature () {
      if (this.paging || !this.tabulator) return
      this.syncData()
    },
    'node.selected' () {
      this.syncSelection()
    },
    signature () {
      this.rebuild()
    }
  },
  mounted () {
    this.build()
  },
  beforeUnmount () {
    this.destroy()
  },
  methods: {
    destroy () {
      if (this.tabulator) {
        this.tabulator.destroy()
        this.tabulator = null
      }
    },
    rebuild () {
      this.destroy()
      this.$nextTick(() => this.build())
    },
    tableRows () {
      return (this.node.resolvedRows || []).map(row => ({
        _key: row.key,
        _row: row.row,
        _actions: row.actions,
        ...row.cells
      }))
    },
    display (value, col) {
      if (typeof value === 'boolean') return value ? '✓' : '–'
      return formatValue(value, {
        format: col && col.format,
        currency: col && col.currency,
        locale: this.locale,
        labels: this.catalog
      })
    },
    columnDefs () {
      const columns = []
      if (this.selectable) {
        columns.push({
          formatter: 'rowSelection',
          titleFormatter: 'rowSelection',
          hozAlign: 'center',
          headerSort: false,
          width: 40,
          cssClass: 'max-pos-datatable__check'
        })
      }
      for (const col of this.node.columns || []) {
        const numeric = col.edit && col.edit.input === 'number' || (col.format && NUMERIC.includes(col.format))
        const def = {
          title: col.label,
          titleFormatter: 'plaintext', // Labels come from merchant-written Max specs
          field: col.key,
          hozAlign: col.align === 'end' || (!col.align && numeric) ? 'right' : (col.align === 'center' ? 'center' : 'left'),
          headerSort: this.sortable || !this.paged,
          width: col.width === 'narrow' ? 72 : undefined,
          minWidth: col.width === 'wide' ? 180 : 80,
          // Tabulator inserts a formatter's string as HTML, and the values are shop data
          formatter: col.edit ? this.editFormatter(col) : (cell) => escapeHtml(this.display(cell.getValue(), col))
        }
        columns.push(def)
      }
      if (this.node.rowActions && this.node.rowActions.length) {
        columns.push({
          title: '',
          field: '_actions',
          headerSort: false,
          hozAlign: 'right',
          width: 120,
          formatter: this.actionsFormatter()
        })
      }
      return columns
    },
    editFormatter (col) {
      const vm = this
      return (cell) => {
        const row = cell.getRow().getData()
        const current = vm.editValue(col, row._key)
        const wrap = document.createElement('div')
        wrap.addEventListener('click', e => e.stopPropagation())
        if (col.edit.input === 'toggle') {
          const input = document.createElement('input')
          input.type = 'checkbox'
          input.checked = current === true
          input.addEventListener('change', () => {
            if (vm.iface.busy.value) {
              input.checked = !input.checked
              return
            }
            vm.iface.setIn(col.edit.state, row._key, input.checked || null)
          })
          wrap.appendChild(input)
          return wrap
        }
        if (col.edit.input === 'select') {
          const select = document.createElement('select')
          select.className = 'form-control input-sm'
          const blank = document.createElement('option')
          blank.value = ''
          blank.textContent = col.edit.placeholder || ''
          select.appendChild(blank)
          for (const option of col.edit.options || []) {
            const item = document.createElement('option')
            item.value = option.value
            item.textContent = option.label
            select.appendChild(item)
          }
          select.value = current ?? ''
          select.addEventListener('change', () => {
            if (vm.iface.busy.value) {
              select.value = vm.editValue(col, row._key) ?? ''
              return
            }
            vm.iface.setIn(col.edit.state, row._key, select.value || null)
          })
          wrap.appendChild(select)
          return wrap
        }
        const input = document.createElement('input')
        input.className = 'form-control input-sm'
        input.type = col.edit.input === 'number' ? 'number' : col.edit.input === 'date' ? 'date' : col.edit.input === 'time' ? 'time' : 'text'
        input.value = current ?? ''
        input.placeholder = col.edit.placeholder ?? (row[col.key] == null ? '' : String(row[col.key]))
        if (col.edit.input === 'time') {
          // The spec gives the step in minutes, the time input takes seconds
          input.step = String(typeof col.edit.step === 'number' && col.edit.step >= 1 ? Math.round(col.edit.step) * 60 : 60)
        } else {
          if (col.edit.min != null) input.min = col.edit.min
          if (col.edit.max != null) input.max = col.edit.max
        }
        input.addEventListener('input', () => {
          if (vm.iface.busy.value) {
            input.value = vm.editValue(col, row._key) ?? ''
            return
          }
          vm.iface.setIn(col.edit.state, row._key, vm.cellValue(col, input))
        })
        wrap.appendChild(input)
        return wrap
      }
    },
    actionsFormatter () {
      const vm = this
      return (cell) => {
        const row = cell.getRow().getData()
        const wrap = document.createElement('div')
        wrap.className = 'text-right'
        wrap.addEventListener('click', e => e.stopPropagation())
        for (const i of row._actions || []) {
          const action = vm.node.rowActions[i]
          if (!action) continue
          const button = document.createElement('button')
          button.type = 'button'
          button.className = 'btn btn-xs ' + (BUTTON_CLASS[action.intent] || 'btn-default')
          button.textContent = action.label
          button.addEventListener('click', ev => {
            ev.stopPropagation()
            // The formatter runs once (often while the interface is still loading), so busy is checked at click time
            if (vm.iface.busy.value) return
            vm.iface.run(action.on && action.on.click, { ...(vm.node.scope || {}), row: row._row })
          })
          wrap.appendChild(button)
        }
        return wrap
      }
    },
    async requestPage (_url, _config, params) {
      this.paging = true
      try {
        const page = Number(params.page) || 1
        const sorter = (params.sort && params.sort[0]) || (params.sorters && params.sorters[0])
        if (this.sortable && sorter && sorter.field) {
          const dir = sorter.dir === 'asc' ? 'asc' : 'desc'
          const changed = this.node.sortValue !== sorter.field || this.node.sortDirValue !== dir
          if (this.node.sortValue !== sorter.field) this.iface.setState(this.node.sort, sorter.field)
          if (this.node.sortDir && this.node.sortDirValue !== dir) this.iface.setState(this.node.sortDir, dir)
          if (changed && this.node.on && this.node.on.sort) await this.iface.run(this.node.on.sort, this.node.scope || {})
        }
        if (this.paged && page !== this.page) {
          this.iface.setState(this.node.pageState, page)
          if (this.node.on && this.node.on.page) await this.iface.run(this.node.on.page, this.node.scope || {})
        }
        await nextTick()
        return { last_page: this.pages, data: this.tableRows() }
      } finally {
        this.paging = false
      }
    },
    async build () {
      if (!this.$refs.table) return
      const Tabulator = await loadTabulator()
      // Unmounted, or built by another call, while Tabulator was loading
      if (!this.$refs.table || this.tabulator) return
      const options = {
        layout: 'fitDataStretch',
        maxHeight: '70vh',
        placeholder: escapeHtml(this.node.empty || this._('Nothing to show.')),
        columns: this.columnDefs(),
        index: '_key',
        selectable: this.selectable,
        selectableCheck: () => false,
        dataLoader: false,
        rowFormatter: this.rowFormatter
      }
      if (this.paged) {
        options.pagination = true
        options.paginationMode = 'remote'
        options.paginationSize = this.pageSize
        options.paginationInitialPage = this.page
        options.sortMode = this.sortable ? 'remote' : 'local'
        options.ajaxURL = 'max'
        options.ajaxRequestFunc = this.requestPage
      } else {
        options.data = this.tableRows()
        options.sortMode = 'local'
      }
      if (this.node.sortValue) {
        options.initialSort = [{ column: this.node.sortValue, dir: this.node.sortDirValue === 'asc' ? 'asc' : 'desc' }]
      }
      this.tabulator = new Tabulator(this.$refs.table, options)
      this.tabulator.on('rowClick', (event, row) => {
        this.openRow(event, row)
      })
      this.tabulator.on('rowSelectionChanged', (data) => {
        if (!this.selectable || this.paging) return
        this.applySelection((data || []).map(item => item._key).filter(Boolean))
      })
      this.$nextTick(() => this.syncSelection())
    },
    syncData () {
      if (!this.tabulator) return
      this.tabulator.replaceData(this.tableRows())
      if (this.paged && this.tabulator.modules && this.tabulator.modules.page) {
        try {
          this.tabulator.setMaxPage(this.pages)
        } catch (e) {}
      }
      this.$nextTick(() => this.syncSelection())
    },
    syncSelection () {
      if (!this.tabulator || !this.selectable) return
      const want = this.selected
      const have = new Set((this.tabulator.getSelectedData() || []).map(item => String(item._key)))
      const same = have.size === want.size && [...have].every(key => want.has(key))
      if (same) return
      this.tabulator.deselectRow()
      const keys = [...want]
      if (keys.length) {
        try {
          this.tabulator.selectRow(keys)
        } catch (e) {}
      }
    },
    rowFormatter (row) {
      const field = this.node.tree && this.node.tree.parentField
      const data = row.getData()
      if (this.node.open) row.getElement().classList.add('max-pos-datatable__open')
      if (field && data._row && data._row[field]) {
        row.getElement().style.paddingLeft = '24px'
      }
    },
    applySelection (keys) {
      this.iface.setSelection(this.node.select, keys, this.node.resolvedRows, this.node.rowKey)
    },
    openRow (event, row) {
      const open = this.node.open
      if (!open || (event.target && event.target.closest && event.target.closest(INTERACTIVE))) return
      const data = row.getData()
      this.iface.run([{ do: 'navigate', to: open.to, id: open.id || { var: 'row.' + this.node.rowKey } }], { ...(this.node.scope || {}), row: data._row })
    },
    editValue (col, key) {
      return this.node.edits && this.node.edits[col.key] ? this.node.edits[col.key][key] : undefined
    },
    cellValue (col, el) {
      if (col.edit.input === 'time') return normalizeTime(el.value)
      if (col.edit.input !== 'number') return el.value
      if (el.value === '') return null
      const n = Number(el.value)
      return Number.isFinite(n) ? n : null
    }
  }
}
</script>

<style scoped>
.max-pos-datatable__title {
  font-weight: 600;
  margin-bottom: 8px;
}
.max-pos-datatable__wrap {
  min-height: 120px;
}
.max-pos-datatable :deep(.tabulator) {
  font-size: 13px;
  background: transparent;
}
.max-pos-datatable :deep(.max-pos-datatable__open) {
  cursor: pointer;
}
.max-pos-datatable__check {
  width: 40px;
}
</style>
